import { QUERY_KEYS } from '~/constants/react-query'
import { Address } from '~/utils/address'
import { captureException } from '~/utils/errors'
import { retryExponentialBackoff } from '~/utils/requests'

import type { QueryClient } from '@tanstack/react-query'
import type { RelayCurrencySearchResult } from '~/hooks/useRelayNetworks'
import type { TokenMetadata } from '~/hooks/useTokenMetadata'
import type { IAddress } from '~/types'

type TokenToFetch = {
  chainId: number
  address: IAddress
  tokenId?: string
}

/**
 * Fetches metadata for multiple tokens using Relay's currency API
 * Uses batch endpoint with tokens parameter, chunking at 50 tokens per request
 */
export const fetchBatchTokenMetadata = async (
  tokens: TokenToFetch[],
  queryClient: QueryClient,
): Promise<TokenMetadata[]> => {
  if (tokens.length === 0) {
    return []
  }

  // Split tokens into chunks of 50
  const CHUNK_SIZE = 50
  const chunks = Array.from(
    { length: Math.ceil(tokens.length / CHUNK_SIZE) },
    (_, i) => tokens.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE),
  )

  // Fetch all chunks in parallel
  const chunkResults = await Promise.all(
    chunks.map((chunk) => fetchBatchChunk(chunk, queryClient)),
  )

  // Flatten results
  return chunkResults.flat()
}

/**
 * Fetches a batch of tokens from Relay API using the tokens parameter
 */
const fetchBatchChunk = async (
  tokens: TokenToFetch[],
  queryClient: QueryClient,
): Promise<TokenMetadata[]> => {
  try {
    // Format tokens as "chainId:address"
    const tokenStrings = tokens.map(
      (token) => `${token.chainId}:${token.address}`,
    )

    const requestBody = {
      tokens: tokenStrings,
      limit: tokens.length,
      includeAllChains: true,
      useExternalSearch: true,
    }

    const response = await retryExponentialBackoff(
      fetch,
      [
        'https://api.relay.link/currencies/v2',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        },
      ],
      {
        maxTry: 3,
      },
    )

    if (!response.ok) {
      captureException(
        new Error(
          `Failed to fetch batch token metadata: ${response.status} ${response.statusText}`,
        ),
      )
      return []
    }

    const data = (await response.json()) as RelayCurrencySearchResult[]

    // Convert to TokenMetadata and cache each token
    const results = data.map((currency) => {
      const metadata: TokenMetadata = {
        address: Address.format(currency.address),
        symbol: currency.symbol,
        decimals: currency.decimals,
        validToken: true,
        logoUri: currency.metadata.logoURI,
        isPosition: false,
        chainId: currency.chainId,
      }

      // Cache individual token
      queryClient.setQueryData(
        [
          QUERY_KEYS.TOKEN_METADATA,
          currency.chainId,
          Address.format(currency.address),
        ],
        metadata,
      )

      return metadata
    })

    return results
  } catch (error) {
    captureException(error)
    return []
  }
}
